import { orderBy } from 'lodash-es'

import { useMutation, useQuery, useQueryClient } from '@fv/client-core'
import { type SpotQuoteGroup, spotContactKeys } from '@fv/client-types'
import { ALL_CONTACTS_GROUP_ID } from '@fv/models'
import type {
  AccountAdminShipmentExportHistory,
  AccountHistory,
  AccountNoteDto,
} from '@fv/models/admin'

import { adminFetch } from '@/utils'

import type { AccountModel, AccountUserModel } from './types'

export type AccountSearchResult = AccountModel & {
  users: AccountUserModel[]
}

export function useSearchAccounts(search: string, enabled = true) {
  return useQuery(
    accountQueryKeys.search(search),
    () =>
      adminFetch<AccountSearchResult[]>(`/accounts`, {
        query: { search },
      }),
    {
      staleTime: Infinity,
      enabled,
    },
  )
}

export function useAccount(id?: string) {
  return useQuery(
    accountQueryKeys.id(id),
    () => adminFetch<AccountModel>(`/accounts/${id}`),
    {
      enabled: !!id,
      refetchOnWindowFocus: false, // account has a lot of data - refetching this every time a window focus changes uses a lot of resources
    },
  )
}

export const accountQueryKeys = {
  all: ['accounts'],
  id: (id?: string) => [...accountQueryKeys.all, ...(id ? [id] : [])],
  search: (search: string) => [...accountQueryKeys.all, 'search', search],
  vendors: (id: string) => ['account-vendors', id],
}

export const useAccountHistory = (accountId?: string) => {
  return useQuery(
    ['account-history', accountId],
    () => adminFetch<AccountHistory[]>(`/accounts/${accountId}/history`),
    {
      enabled: !!accountId,
    },
  )
}

export const useAccountExportHistory = (accountId?: string) => {
  return useQuery(
    ['account-export-history', accountId],
    () =>
      adminFetch<AccountAdminShipmentExportHistory[]>(
        `/accounts/${accountId}/shipment-export-history`,
      ),
    {
      enabled: !!accountId,
    },
  )
}

export const triggerInvoiceAdded = (accountId?: string) => {
  return adminFetch(`/accounts/${accountId}/invoice-added`, { method: 'POST' })
}

export const useAccountNotes = (accountId?: string) => {
  return useQuery(
    ['account-notes', accountId],
    () => adminFetch<AccountNoteDto[]>(`/accounts/${accountId}/notes`),
    {
      enabled: !!accountId,
      select: data =>
        data.map(d => ({
          ...d,
          date_created: new Date(d.date_created),
        })),
    },
  )
}

type CreateNoteArgs = {
  accountId: string
  note: string
}
export const useCreateAccountNote = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, ...body }: CreateNoteArgs) =>
      adminFetch(`/accounts/${accountId}/notes`, { method: 'POST', body }),
    onSettled(data, err, { accountId }) {
      queryClient.invalidateQueries({ queryKey: ['account-notes', accountId] })
    },
  })
}

const fetchSpotQuoteGroups = (accountId: string) =>
  adminFetch<SpotQuoteGroup[]>(`/accounts/${accountId}/spot-quote-groups`)

const normalizeGroups = (groups: SpotQuoteGroup[]) =>
  groups.map(g => ({
    ...g,
    users: g.users.filter(u => !!u.email),
  }))

function sortAndFilter(groups: SpotQuoteGroup[]) {
  const orderedGroups = orderBy(groups ?? [], [
    i => i.groupId !== ALL_CONTACTS_GROUP_ID,
    i => i.groupName.toLowerCase(),
  ])
  return normalizeGroups(orderedGroups)
}

export function useSpotQuoteGroups(accountId: string) {
  return useQuery(
    [spotContactKeys.all, accountId],
    () => fetchSpotQuoteGroups(accountId),
    {
      select: sortAndFilter,
      enabled: true,
    },
  )
}

export function useSpotQuoteContacts(accountId: string) {
  const query = useSpotQuoteGroups(accountId)
  return {
    ...query,
    data: query.data?.find(g => g.groupId === ALL_CONTACTS_GROUP_ID)?.users,
  }
}
